var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { toValue } from "@vueuse/core";
import { computed, del, ref, set, unref } from "vue";
/**
 * Returns true if the item is not defined.
 * @param item The item to check.
 */
const fetchIfAbsent = (item) => !item;
/**
 * A composable that provides a simple key-value cache for items fetched from the server.
 *
 * Useful for storing items that are fetched by id.
 *
 * @param fetchItemHandler Fetches an item from the server.
 * @param shouldFetchHandler Returns true if the item should be fetched.
 * Provides fine-grained control over when to fetch an item.
 * If not provided, by default, the item will be fetched if it is not already stored.
 */
export function useKeyedCache(fetchItemHandler, shouldFetchHandler) {
    const storedItems = ref({});
    const loadingItem = ref({});
    const getItemById = computed(() => {
        return (id) => {
            const item = storedItems.value[id];
            if (shouldFetch(item)) {
                fetchItemById({ id: id });
            }
            return item !== null && item !== void 0 ? item : null;
        };
    });
    function shouldFetch(item) {
        if (shouldFetchHandler == undefined) {
            return fetchIfAbsent(item);
        }
        return toValue(shouldFetchHandler)(item);
    }
    const isLoadingItem = computed(() => {
        return (id) => {
            var _a;
            return (_a = loadingItem.value[id]) !== null && _a !== void 0 ? _a : false;
        };
    });
    function fetchItemById(params) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const itemId = params.id;
            const isAlreadyLoading = (_a = loadingItem.value[itemId]) !== null && _a !== void 0 ? _a : false;
            if (isAlreadyLoading) {
                return;
            }
            set(loadingItem.value, itemId, true);
            try {
                const fetchItem = unref(fetchItemHandler);
                const { data } = yield fetchItem({ id: itemId });
                set(storedItems.value, itemId, data);
                return data;
            }
            finally {
                del(loadingItem.value, itemId);
            }
        });
    }
    return {
        /**
         * The stored items as a reactive object.
         */
        storedItems,
        /**
         * A computed function that returns the item with the given id.
         * If the item is not already stored, it will be fetched from the server.
         * And reactively updated when the fetch completes.
         */
        getItemById,
        /**
         * A computed function that returns true if the item with the given id is currently being fetched.
         */
        isLoadingItem,
        /**
         * Fetches the item with the given id from the server.
         * And reactively updates the stored item when the fetch completes.
         */
        fetchItemById,
    };
}
